import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { templateService } from '../services/templateService';
import { providerKeys } from './useProviders';
import { tokenKeys } from './useTokens';
import type { ProviderTemplate } from '../types';

// Query keys
export const templateKeys = {
  all: ['templates'] as const,
  lists: () => [...templateKeys.all, 'list'] as const,
  list: (filters?: any) => [...templateKeys.lists(), filters] as const,
  details: () => [...templateKeys.all, 'detail'] as const,
  detail: (id: string) => [...templateKeys.details(), id] as const,
  byType: (type: string) => [...templateKeys.all, 'type', type] as const,
};

/**
 * Hook to fetch all available templates
 */
export const useTemplates = () => {
  return useQuery({
    queryKey: templateKeys.list(),
    queryFn: () => templateService.getTemplates(),
    staleTime: 300000, // Consider templates fresh for 5 minutes (rarely change)
  });
};

/**
 * Hook to fetch a specific template
 */
export const useTemplate = (templateId: string, enabled = true) => {
  return useQuery({
    queryKey: templateKeys.detail(templateId),
    queryFn: () => templateService.getTemplate(templateId),
    enabled,
    staleTime: 300000,
  });
};

/**
 * Hook to fetch templates by provider type
 */
export const useTemplatesByType = (providerType: string, enabled = true) => {
  return useQuery({
    queryKey: templateKeys.byType(providerType),
    queryFn: () => templateService.getTemplatesByType(providerType),
    enabled,
    staleTime: 300000,
  });
};

/**
 * Hook to create a new provider template
 */
export const useCreateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Partial<ProviderTemplate>) => templateService.createTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
    },
  });
};

/**
 * Hook to update an existing provider template
 */
export const useUpdateTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ templateId, data }: { templateId: string; data: Partial<ProviderTemplate> }) =>
      templateService.updateTemplate(templateId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: templateKeys.detail(variables.templateId) });
    },
  });
};

/**
 * Hook to delete a provider template
 */
export const useDeleteTemplate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (templateId: string) => templateService.deleteTemplate(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: templateKeys.lists() });
      // Also invalidate providers so template removal reflects immediately
      queryClient.invalidateQueries({ queryKey: providerKeys.lists() });
      // Tokens depend on provider/template display
      queryClient.invalidateQueries({ queryKey: tokenKeys.lists() });
    },
  });
};
